import React, { useEffect, useMemo, useState } from 'react';

import { CheckpointPass } from '../../../../../../types/common.types';

import useToaster from '../../../../../../hooks/use-toaster/useToaster';

import { ARCHIVED } from '../../../../../../utils/constants';
import { getClassNames } from '../../../../../../utils/util';

import Button from '../../../../../common/button/Button';
import TextInput from '../../../../../common/input/text-input/TextInput';
import Modal from '../../../../../common/modal/Modal';
import AccountabilityConfigurePassNameModal from '../accountability-configure-pass-name-modal/AccountabilityConfigurePassNameModal';

import './accountability-configure-passes-modal.scss';

type Props = {
  status: string;
  show: boolean;
  close: () => void;
  passes: CheckpointPass[];
  savePasses: (passes: CheckpointPass[]) => void;
};

const AccountabilityConfigurePassesModal = ({ status, show, close, passes, savePasses }: Props) => {
  const [passesToEdit, setPassesToEdit] = useState<CheckpointPass[]>([]);
  const [showPassNameModal, setShowPassNameModal] = useState(false);

  const { showToaster } = useToaster();

  useEffect(() => {
    if (!show) {
      setPassesToEdit([]);
      return;
    }

    const copy = structuredClone(passes);
    copy.sort((passOne, passTwo) => passOne.index - passTwo.index);
    setPassesToEdit(copy);
  }, [passes, show]);

  const nameType = useMemo(() => {
    if (passesToEdit.every((pass) => pass.name.split(' ')[0] === 'Lap')) {
      return 'Lap';
    } else if (passesToEdit.every((pass) => pass.name.split(' ')[0] === 'Round')) {
      return 'Round';
    } else {
      return 'Other';
    }
  }, [passesToEdit]);

  const handleNameTypeChange = (name: string) => {
    if (name === 'Other') {
      setShowPassNameModal(true);
      return;
    }

    const newPasses = passesToEdit.map((pass) => ({
      ...pass,
      name: `${name} ${pass.index}`
    }));

    setPassesToEdit(newPasses);
  };

  const handleNameChange = (index: number, newName: string) => {
    const newPasses = passesToEdit.map((pass) => {
      if (pass.index === index) {
        return { ...pass, name: newName };
      }
      return pass;
    });

    setPassesToEdit(newPasses);
  };

  const closeModal = () => {
    close();
  };

  const handleSaveClick = () => {
    if (passesToEdit.some((pass) => pass.name.trim() === '')) {
      showToaster('error', 'Pass names cannot be empty');
      return;
    }

    savePasses(passesToEdit);
    closeModal();
  };

  return (
    <>
      <Modal show={show && !showPassNameModal}>
        <div className="accountability-configure-passes-modal">
          <div className="accountability-configure-passes-modal__header">Pass Names</div>
          <div className="accountability-configure-passes-modal__content">
            <div className="accountability-configure-passes-modal__shortcuts">
              <p>Default Names</p>
              <div>
                <button
                  className={getClassNames('accountability-configure-passes-modal__shortcut', {
                    active: nameType === 'Lap'
                  })}
                  onClick={() => handleNameTypeChange('Lap')}
                  disabled={status === ARCHIVED}
                >
                  Lap
                </button>
                <button
                  className={getClassNames('accountability-configure-passes-modal__shortcut', {
                    active: nameType === 'Round'
                  })}
                  onClick={() => handleNameTypeChange('Round')}
                  disabled={status === ARCHIVED}
                >
                  Round
                </button>
                <button
                  className={getClassNames('accountability-configure-passes-modal__shortcut', {
                    active: nameType === 'Other'
                  })}
                  onClick={() => handleNameTypeChange('Other')}
                  disabled={status === ARCHIVED}
                >
                  Other
                </button>
              </div>
            </div>
            <div className="accountability-configure-passes-modal__passes">
              {passesToEdit.map((pass) => (
                <div key={pass.index} className="accountability-configure-passes-modal__pass">
                  <TextInput
                    label={`Pass ${pass.index}`}
                    value={pass.name}
                    onChange={({ target }) => handleNameChange(pass.index, target.value)}
                    name={`pass-${pass.index}`}
                    disabled={status === ARCHIVED}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="accountability-configure-passes-modal__button-container">
            <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
            {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
          </div>
        </div>
      </Modal>

      <AccountabilityConfigurePassNameModal
        show={showPassNameModal}
        close={() => setShowPassNameModal(false)}
        onSaveClick={(name) => handleNameTypeChange(name)}
      />
    </>
  );
};

export default AccountabilityConfigurePassesModal;
